import { useState, useEffect, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { DesignSystemProvider, Toaster, type DesignSystemProviderProps } from '@rammetec/design-system'
import { toUiPreferences } from '@rammetec/app-kit/preferences'
import {
  AuthServiceProvider,
  RepositoryResolverProvider,
  SessionProvider,
  ThemePreferenceProvider,
  UserPreferencesProvider,
  useCurrentUser,
  useSessionManager,
  useThemePreference,
  useUserPreferences,
} from '@vectorhub/application'
import { rtLogService } from '@rammetec/core'
import { bootstrapDataAccess } from '@/services/data-access.bootstrap'
import { preferencesService } from '@/services/preferences.bootstrap'
import { themePreferenceStorage } from '@/services/themePreferenceStorage'
import { userPreferencesStorage } from '@/services/userPreferencesStorage'
import { i18n } from '@/services/i18n.bootstrap'
import { getLogContextOrgId, setLogContextOrgId } from '@/services/logContextStore'
import { createConsoleLogHttpTransport } from '@/services/logHttpTransport'
import { fetchLogConfig } from '@/services/logApi'
import { env } from '@/config/env'
import { appInfo } from '@/config/appInfo'
import { authService } from '@/authService'
import { ModuleResolverBridge } from '@/app/modules/ModuleResolverBridge'
import { PreferencesBridge } from '@/app/PreferencesBridge'

const LOG_LEVEL_POLL_MS = 10 * 60 * 1000
const logPersistEnabled =
  env.appEnvironment !== 'development' || env.logPersistInDev

// Called at module level so data-access is ready before the React tree mounts.
bootstrapDataAccess()

rtLogService.init({
  transport: createConsoleLogHttpTransport(),
  app: 'vh-console',
  appVersion: appInfo.version,
  getContext: () => ({
    orgId: getLogContextOrgId() ?? undefined,
    route: typeof location !== 'undefined' ? location.pathname : undefined,
  }),
  persist: logPersistEnabled,
})

/** Keeps `getContext().orgId` in sync with the selected organization (sync read). */
function LogContextBridge({ children }: { children: ReactNode }) {
  const { currentOrgId } = useSessionManager()
  useEffect(() => {
    setLogContextOrgId(currentOrgId)
  }, [currentOrgId])
  return children
}

/**
 * Applies org debug grants to `rtLogService.setMinLevel` (request 0061).
 * Polls every 10 min; schedules expiry from `until`. Failures stay at `info`.
 */
function LogLevelBridge({ children }: { children: ReactNode }) {
  const { currentOrgId } = useSessionManager()
  const { user } = useCurrentUser()
  const currentUserId = user?.userId

  useEffect(() => {
    if (!logPersistEnabled || !currentUserId || !currentOrgId) {
      rtLogService.setMinLevel('info')
      return
    }

    let cancelled = false
    let expireTimer: ReturnType<typeof setTimeout> | undefined
    let pollTimer: ReturnType<typeof setInterval> | undefined

    const clearExpire = () => {
      if (expireTimer !== undefined) {
        clearTimeout(expireTimer)
        expireTimer = undefined
      }
    }

    const applyConfig = async () => {
      clearExpire()
      rtLogService.setMinLevel('info')
      try {
        const config = await fetchLogConfig(currentOrgId)
        if (cancelled) return
        if (config.minLevel === 'debug') {
          rtLogService.setMinLevel('debug')
          if (config.until) {
            const ms = Date.parse(config.until) - Date.now()
            if (Number.isFinite(ms) && ms > 0) {
              expireTimer = setTimeout(() => {
                rtLogService.setMinLevel('info')
              }, ms)
            } else {
              rtLogService.setMinLevel('info')
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          rtLogService.setMinLevel('info')
          rtLogService.debug('[log-level] failed to fetch /log/v1/config', { error })
        }
      }
    }

    void applyConfig()
    pollTimer = setInterval(() => {
      void applyConfig()
    }, LOG_LEVEL_POLL_MS)

    return () => {
      cancelled = true
      clearExpire()
      if (pollTimer !== undefined) clearInterval(pollTimer)
      rtLogService.setMinLevel('info')
    }
  }, [currentUserId, currentOrgId])

  return children
}

/** Sync UiPreferences adapter over the preferences singleton (works pre-hydrate). */
const uiPreferences = toUiPreferences(preferencesService)

function ThemedDesignSystem({
  brand,
  children,
}: {
  brand: DesignSystemProviderProps['brand']
  children: ReactNode
}) {
  const { mode } = useThemePreference()
  return (
    <DesignSystemProvider brand={brand} mode={mode} uiPreferences={uiPreferences}>
      {children}
    </DesignSystemProvider>
  )
}

function I18nLanguageBridge({ children }: { children: ReactNode }) {
  const { language } = useUserPreferences()
  useEffect(() => {
    if (i18n.language !== language) void i18n.changeLanguage(language)
  }, [language])
  return <>{children}</>
}

export interface AppProvidersProps {
  children: ReactNode
}

export function AppProviders({ children }: AppProvidersProps) {
  const [queryClient] = useState(() => new QueryClient())

  return (
    <ThemePreferenceProvider
      initial={env.designSystemMode}
      storage={themePreferenceStorage}
    >
      <ThemedDesignSystem brand={env.designSystemBrand}>
        <QueryClientProvider client={queryClient}>
          <AuthServiceProvider value={authService}>
            <UserPreferencesProvider storage={userPreferencesStorage}>
              <I18nLanguageBridge>
                <SessionProvider>
                  <LogContextBridge>
                    <LogLevelBridge>
                      <PreferencesBridge>
                        <RepositoryResolverProvider>
                          <ModuleResolverBridge>
                            {children}
                          </ModuleResolverBridge>
                          <Toaster />
                        </RepositoryResolverProvider>
                      </PreferencesBridge>
                    </LogLevelBridge>
                  </LogContextBridge>
                </SessionProvider>
              </I18nLanguageBridge>
            </UserPreferencesProvider>
          </AuthServiceProvider>
        </QueryClientProvider>
      </ThemedDesignSystem>
    </ThemePreferenceProvider>
  )
}
